import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandPanel from "../components/BrandPanel";
import LangToggle from "../components/LangToggle";
import { useLang } from "../context/LangContext";
import { UserCheck, Lock, Building2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function EmployeeRegister() {
    const { t, lang, toggle } = useLang();
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [employeeName, setEmployeeName] = useState("");
    const [companyCode, setCompanyCode] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!employeeName.trim() || !companyCode.trim() || !password) {
            setError(t.accountTypeRequired);
            return;
        }
        if (password !== confirm) {
            setError(t.passwordMismatch);
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/employee/register", {
                employee_name: employeeName.trim(),
                password,
                company_code: companyCode.trim().toUpperCase(),
            });
            await refresh();
            navigate("/");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const iconSide = lang === "ar" ? { right: "0.75rem" } : { left: "0.75rem" };
    const inputPad = lang === "ar" ? "h-11 pe-10 ps-3" : "h-11 ps-10 pe-3";

    return (
        <div className="min-h-screen app-bg" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="grid min-h-screen lg:grid-cols-2">
                <BrandPanel />
                <div className="flex items-center justify-center px-4 py-8 sm:px-8 sm:py-12">
                    <div className="w-full max-w-md">
                        <div className="mb-6 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Link to="/login" className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 shadow-sm hover:bg-neutral-50" data-testid="emp-reg-home-btn">
                                    {t.accountTypeBackHome}
                                </Link>
                                <Link to="/register/type" className="text-sm font-medium text-neutral-500 hover:text-neutral-900" data-testid="emp-reg-back">
                                    ← {t.accountTypePickerTitle}
                                </Link>
                            </div>
                            <LangToggle lang={lang} onToggle={toggle} t={t} />
                        </div>

                        <h2 className="mb-1 font-heading text-2xl font-bold sm:text-3xl">{t.employeeRegisterTitle}</h2>
                        <p className="mb-6 text-sm text-neutral-500">{t.accountTypeEmployeeDesc}</p>

                        <form onSubmit={submit} className="space-y-4" data-testid="emp-register-form">
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.employeeNameLabel}</Label>
                                <div className="relative">
                                    <UserCheck className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        value={employeeName}
                                        onChange={(e) => setEmployeeName(e.target.value)}
                                        className={inputPad}
                                        placeholder={t.employeeNamePlaceholder}
                                        required
                                        data-testid="emp-name-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.employeeCodeLabel}</Label>
                                <div className="relative">
                                    <Building2 className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        value={companyCode}
                                        onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                                        className={`${inputPad} font-numbers uppercase tracking-wider`}
                                        placeholder={t.employeeCodePlaceholder}
                                        required
                                        data-testid="emp-code-input"
                                        dir="ltr"
                                    />
                                </div>
                            </div>


                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.passwordLabel}</Label>
                                <div className="relative">
                                    <Lock className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={inputPad}
                                        placeholder={t.passwordPlaceholder}
                                        required
                                        data-testid="emp-password-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.confirmPasswordLabel}</Label>
                                <div className="relative">
                                    <Lock className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        type="password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className={inputPad}
                                        placeholder={t.passwordPlaceholder}
                                        required
                                        data-testid="emp-confirm-input"
                                    />
                                </div>
                            </div>

                            {error ? (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="emp-error">
                                    {error}
                                </div>
                            ) : null}

                            <Button
                                type="submit"
                                disabled={loading}
                                className="h-12 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                                data-testid="emp-register-submit"
                            >
                                {loading ? "…" : t.createAccount}
                            </Button>
                        </form>

                        <p className="mt-4 text-center text-sm text-neutral-500">
                            <Link to="/employee/login" className="font-semibold text-amber-700 hover:underline" data-testid="emp-login-link">
                                {t.employeeLoginLink}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
